import { useQuery } from "@tanstack/vue-query";
import type { MaybeRef } from "vue";
import { computed, unref } from "vue";
import { customInstance } from "@/custom-axios";
import type { ErrorType } from "@/custom-axios";

type ReportType = "meals_to_release" | "nested_meals_to_release" | "presences_list" | "labels";

export interface ArchivedReport {
  id: string;
  automatedReportId: number;
  name: string;
  type: ReportType;
  generatedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedArchivedReportsList {
  items: ArchivedReport[];
  currentPage: number;
  perPage: number;
  totalPages: number;
  totalResults: number;
}

interface GetArchivedReportsParams {
  automatedReportId: string;
  page: number;
  perPage?: number;
}

const getArchivedReports = async (
  params: GetArchivedReportsParams,
): Promise<PaginatedArchivedReportsList> => {
  const response = await customInstance<{ data: PaginatedArchivedReportsList }>(
    "/api/frontend/automatedReports/archived",
    {
      method: "GET",
      params: {
        automatedReportId: params.automatedReportId,
        page: params.page,
        perPage: params.perPage ?? 10,
      },
    },
  );

  return response.data;
};

export const getArchivedReportsQueryKey = (
  automatedReportId: MaybeRef<string>,
  page: MaybeRef<number>,
) => ["archivedReports", automatedReportId, page] as const;

export function useGetArchivedReportsQuery(
  automatedReportId: MaybeRef<string>,
  page: MaybeRef<number>,
  perPage: number = 10,
) {
  const queryKey = computed(() =>
    getArchivedReportsQueryKey(unref(automatedReportId), unref(page)),
  );

  return useQuery<PaginatedArchivedReportsList, ErrorType<unknown>>({
    queryKey,
    queryFn: () =>
      getArchivedReports({
        automatedReportId: unref(automatedReportId),
        page: unref(page),
        perPage,
      }),
  });
}
